import type { AxiosError } from 'axios'
import type { ApiErrorResponse } from '@/core/api/types'

export class ApiException extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, string[]>
  readonly timestamp: string
  readonly path?: string
  readonly isNetworkError: boolean
  readonly isTimeout: boolean
  readonly isUnauthorized: boolean
  readonly isForbidden: boolean
  readonly isNotFound: boolean
  readonly isValidationError: boolean
  readonly isServerError: boolean

  constructor(
    message: string,
    options: {
      code?: string
      status?: number
      details?: Record<string, string[]>
      timestamp?: string
      path?: string
      isNetworkError?: boolean
      isTimeout?: boolean
    } = {}
  ) {
    super(message)
    this.name = 'ApiException'
    this.code = options.code ?? 'UNKNOWN_ERROR'
    this.status = options.status ?? 0
    this.details = options.details
    this.timestamp = options.timestamp ?? new Date().toISOString()
    this.path = options.path
    this.isNetworkError = options.isNetworkError ?? false
    this.isTimeout = options.isTimeout ?? false
    this.isUnauthorized = this.status === 401
    this.isForbidden = this.status === 403
    this.isNotFound = this.status === 404
    this.isValidationError = this.status === 400 || this.status === 422
    this.isServerError = this.status >= 500

    Object.setPrototypeOf(this, ApiException.prototype)
  }

  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiException {
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return new ApiException('Error de conexión. Verifica tu conexión a internet.', {
        code: 'NETWORK_ERROR',
        status: 0,
        isNetworkError: true,
      })
    }

    if (error.code === 'ECONNABORTED') {
      return new ApiException('La solicitud tardó demasiado. Intenta de nuevo.', {
        code: 'TIMEOUT_ERROR',
        status: 0,
        isTimeout: true,
      })
    }

    const { response } = error
    const data = response.data

    return new ApiException(data?.message ?? ApiException.getDefaultMessage(response.status), {
      code: data?.code ?? data?.errorCode ?? `HTTP_${response.status}`,
      status: response.status,
      details: data?.details,
      timestamp: data?.timestamp,
      path: data?.path ?? error.config?.url,
    })
  }

  private static getDefaultMessage(status: number): string {
    const messages: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado. Inicia sesión nuevamente.',
      403: 'No tienes permisos para realizar esta acción',
      404: 'Recurso no encontrado',
      409: 'Conflicto con el estado actual del recurso',
      422: 'Error de validación',
      429: 'Demasiadas solicitudes. Espera un momento.',
      500: 'Error interno del servidor',
      502: 'Error de conexión con el servidor',
      503: 'Servicio no disponible temporalmente',
      504: 'El servidor no respondió a tiempo',
    }
    return messages[status] ?? 'Error desconocido'
  }

  getFieldError(field: string): string | undefined {
    return this.details?.[field]?.[0]
  }

  getAllFieldErrors(): Record<string, string> {
    if (!this.details) return {}
    return Object.entries(this.details).reduce(
      (acc, [key, messages]) => {
        const firstMessage = messages[0]
        if (firstMessage) {
          acc[key] = firstMessage
        }
        return acc
      },
      {} as Record<string, string>
    )
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      details: this.details,
      timestamp: this.timestamp,
      path: this.path,
    }
  }
}

export function isApiException(error: unknown): error is ApiException {
  return error instanceof ApiException
}
